import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PlusCircle, Upload, X, Loader2, Camera, CalendarDays, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { currentMonth } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { VoiceButton } from "../components/VoiceButton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function AddExpense() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { household, role } = useAuth();
  const fileRef = useRef();

  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [category, setCategory] = useState("Groceries");
  const [notes, setNotes] = useState("");
  const [isBill, setIsBill] = useState(false);
  const [preview, setPreview] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [nlText, setNlText] = useState("");
  const [nlBusy, setNlBusy] = useState(false);
  const [catTouched, setCatTouched] = useState(false);
  const [aiCat, setAiCat] = useState(null);
  const [catBusy, setCatBusy] = useState(false);

  const categories = household?.categories || [];

  useEffect(() => {
    if (catTouched || name.trim().length < 3) return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      setCatBusy(true);
      try {
        const { data } = await api.post("/ai/suggest-category", { name: name.trim(), notes });
        if (!cancelled && data?.category) {
          setCategory(data.category);
          setAiCat(data.category);
        }
      } catch {
        // suggestion is best-effort
      } finally {
        if (!cancelled) setCatBusy(false);
      }
    }, 900);
    return () => { cancelled = true; clearTimeout(t); setCatBusy(false); };
  }, [name, notes, catTouched]);

  const pickCategory = (c) => {
    setCategory(c);
    setCatTouched(true);
    setAiCat(null);
  };

  const applyParsed = (d) => {
    if (d?.name) setName(d.name);
    if (d?.amount) setAmount(String(d.amount));
    if (d?.date) setDate(d.date);
    if (d?.category) {
      setCategory(d.category);
      setAiCat(d.category);
      setCatTouched(true);
    }
  };

  const parseNl = async (override) => {
    const text = (typeof override === "string" ? override : nlText).trim();
    if (!text) return toast.error("Describe the expense first");
    setNlBusy(true);
    try {
      const { data } = await api.post("/ai/parse-expense", { text });
      applyParsed(data);
      toast.success("Filled from your description — review & submit");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not understand that");
    } finally {
      setNlBusy(false);
    }
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) return toast.error("Image too large (max 12MB)");
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/files/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      setFileId(data.file_id);
      toast.success("Receipt uploaded");
    } catch {
      toast.error("Upload failed");
      setPreview(null);
      setUploading(false);
      return;
    }
    setUploading(false);
    // AI auto-scan the same image to pre-fill fields
    setScanning(true);
    try {
      const sf = new FormData();
      sf.append("file", file);
      const { data } = await api.post("/ai/scan-receipt", sf, { headers: { "Content-Type": "multipart/form-data" } });
      applyParsed(data);
      toast.success("Receipt scanned — review the details ✨");
    } catch (e) {
      toast(e.response?.data?.detail || "Couldn't auto-read the receipt — enter details manually");
    } finally {
      setScanning(false);
    }
  };

  const removeReceipt = () => {
    setPreview(null);
    setFileId(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    if (!name.trim()) return toast.error("Enter an expense name");
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    setSubmitting(true);
    try {
      await api.post("/expenses", {
        name: name.trim(), amount: amt, date, category, notes: notes.trim(),
        receipt_file_id: fileId, is_bill: isBill,
      });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["expenses"] });
      toast.success(`${isBill ? "Bill" : "Expense"} added & split with roommates`);
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add expense");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto space-y-5">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
          <PlusCircle className="h-6 w-6 text-primary" /> Add {isBill ? "a bill" : "an expense"}
        </h1>
        <p className="text-sm text-muted-foreground mt-0.5">Split equally among all roommates automatically.</p>
      </div>

      <div className="rounded-3xl border border-primary/30 bg-primary/5 p-4" data-testid="ai-quick-add">
        <div className="flex items-center gap-2 mb-2"><Sparkles className="h-4 w-4 text-primary" /><span className="font-semibold text-sm">Quick add with AI</span></div>
        <div className="flex gap-2">
          <Input value={nlText} onChange={(e) => setNlText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && parseNl()} placeholder='e.g. "Spent 45 on groceries yesterday"' className="h-11 rounded-xl bg-card" data-testid="ai-nl-input" />
          <VoiceButton disabled={nlBusy} onTranscript={(t) => { setNlText(t); parseNl(t); }} />
          <Button onClick={() => parseNl()} disabled={nlBusy} className="h-11 rounded-xl shrink-0" data-testid="ai-nl-btn">
            {nlBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Wand2 className="h-4 w-4 mr-1.5" /> Fill</>}
          </Button>
        </div>
      </div>

      <div className="rounded-3xl border border-border bg-card p-6 space-y-5" data-testid="add-expense-form">
        {role === "admin" && (
          <div className="flex items-center justify-between rounded-2xl bg-secondary/60 p-3.5">
            <div>
              <div className="font-semibold text-sm">Household bill</div>
              <div className="text-xs text-muted-foreground">Rent, utilities etc. added by admin</div>
            </div>
            <Switch checked={isBill} onCheckedChange={setIsBill} data-testid="is-bill-toggle" />
          </div>
        )}

        <div>
          <Label htmlFor="exp-name" className="text-xs">Name / description</Label>
          <Input id="exp-name" data-testid="expense-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Weekly groceries" className="mt-1.5 h-11 rounded-xl" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="exp-amount" className="text-xs">Amount ({household?.currency || "USD"})</Label>
            <Input id="exp-amount" data-testid="expense-amount-input" type="number" inputMode="decimal" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="mt-1.5 h-11 rounded-xl font-mono" />
          </div>
          <div>
            <Label htmlFor="exp-date" className="text-xs flex items-center gap-1"><CalendarDays className="h-3 w-3" /> Date</Label>
            <Input id="exp-date" data-testid="expense-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5 h-11 rounded-xl" />
          </div>
        </div>

        <div>
          <Label className="text-xs flex items-center gap-1.5">Category
            {catBusy && <Loader2 className="h-3 w-3 animate-spin text-primary" data-testid="ai-category-loading" />}
            {!catBusy && aiCat === category && <span className="inline-flex items-center gap-0.5 rounded-full bg-primary/10 text-primary px-1.5 py-0.5 text-[10px] font-semibold" data-testid="ai-category-badge"><Sparkles className="h-2.5 w-2.5" /> AI suggested</span>}
          </Label>
          <Select value={category} onValueChange={pickCategory} onOpenChange={(o) => { if (o) setCatTouched(true); }}>
            <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="expense-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {categories.map((c) => <SelectItem key={c} value={c} data-testid={`category-option-${c}`}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="exp-notes" className="text-xs">Notes (optional)</Label>
          <Textarea id="exp-notes" data-testid="expense-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything to add…" className="mt-1.5 rounded-xl resize-none" rows={2} />
        </div>

        <div>
          <Label className="text-xs">Receipt photo (optional)</Label>
          {preview ? (
            <div className="mt-1.5 relative rounded-2xl overflow-hidden border border-border">
              <img src={preview} alt="Receipt preview" className="w-full max-h-64 object-contain bg-secondary" />
              {(uploading || scanning) && <div className="absolute inset-0 bg-background/70 flex flex-col items-center justify-center gap-2"><Loader2 className="h-6 w-6 animate-spin text-primary" /><span className="text-xs font-medium text-primary">{scanning ? "Reading receipt…" : "Uploading…"}</span></div>}
              <button onClick={removeReceipt} className="absolute top-2 right-2 h-8 w-8 rounded-full bg-background/90 border border-border flex items-center justify-center" data-testid="remove-receipt-btn"><X className="h-4 w-4" /></button>
            </div>
          ) : (
            <button onClick={() => fileRef.current?.click()} className="mt-1.5 w-full rounded-2xl border-2 border-dashed border-border p-6 flex flex-col items-center gap-2 text-muted-foreground hover:border-primary hover:text-primary transition-colors" data-testid="receipt-upload-trigger">
              <Camera className="h-7 w-7" />
              <span className="text-sm font-medium">Tap to add a receipt photo</span>
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onPickFile} className="hidden" data-testid="receipt-upload-input" />
        </div>

        <Button onClick={submit} disabled={submitting || uploading} data-testid="submit-expense-btn" className="w-full h-12 rounded-xl text-base font-semibold shadow-md shadow-primary/20">
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Upload className="h-4 w-4 mr-2" /> Add {isBill ? "bill" : "expense"}</>}
        </Button>
      </div>
    </div>
  );
}
